import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Center,
  Heading,
  HStack,
  Icon,
  Spinner,
  Text,
  VStack,
  Radio,
  RadioGroup,
  useToast,
  Tabs,
  TabList,
  Tab,
} from "@chakra-ui/react";
import { FaTruck, FaTimes, FaSortAmountDown, FaSortAmountUp } from "react-icons/fa";
import FirstLevelCard from "./components/FirstLevelCard";
import { getFirstLevelEntities, groupingLabel } from "./service";
import {
  CityScope,
  FirstLevelEntity,
  ShipmentFilters,
  ShipmentGrouping,
  SortOrder,
  STANDARD_CPT_SLAB_LEVEL,
} from "./types";
import { FcShipped } from "react-icons/fc";
import {
  ApiConfigProvider,
  setGlobalApiConfig,
  ApiConfigProviderProps,
} from "../../config/ApiConfig";
import ShipmentFiltersBar from "./components/ShipmentFilters";
import { useCptSlabLevels } from "./hooks/useCptSlabLevels";
import { CPT_SLAB_META, FASTEST_CPT_SLAB_LEVEL } from "../DispatchDashboard/utils";

export default function ShipmentsDashboard(props: ApiConfigProviderProps) {
  const { baseUrl, authToken, clientId, customFilters } = props;
  const [expandedFirstLevel, setExpandedFirstLevel] = useState<Set<string>>(
    new Set()
  );
  const [firstLevelEntities, setFirstLevelEntities] = useState<
    FirstLevelEntity[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [grouping, setGrouping] = useState<ShipmentGrouping>("customer");
  const [activeTab, setActiveTab] = useState<
    "undelivered" | "delivered" | "cancelled"
  >("undelivered");
  const [activeCptTab, setActiveCptTab] = useState<string>(FASTEST_CPT_SLAB_LEVEL);
  const [cityScope, setCityScope] = useState<CityScope>("intra");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [stats, setStats] = useState<{
    totalUndeliveredShipmentsCount: number;
    totalDeliveredShipmentsCount: number;
    totalCancelledShipmentsCount: number;
  }>({
    totalUndeliveredShipmentsCount: 0,
    totalDeliveredShipmentsCount: 0,
    totalCancelledShipmentsCount: 0,
  });
  const [isApiConfigReady, setIsApiConfigReady] = useState(false);
  const [dispatchModeFilter, setDispatchModeFilter] = useState<{
    dispatchMode?: string | null;
  }>({
    dispatchMode: undefined,
  });
  const toast = useToast();
  const { levels } = useCptSlabLevels();

  const sortedLevelKeys = useMemo(() => {
    if (!levels) return [];

    const priorityMap: Record<string, number> = {
      "1": 1,
      "2": 2,
      "-2": 3,
      "-1": 4,
    };

    return Object.keys(levels).sort((a, b) => {
      const priorityA = priorityMap[a] ?? 99;
      const priorityB = priorityMap[b] ?? 99;
      return priorityA - priorityB;
    });
  }, [levels]);

  const isStandardDelivery =
    Number(activeCptTab) === STANDARD_CPT_SLAB_LEVEL;

  const filters: ShipmentFilters = useMemo(() => {
    const next: ShipmentFilters = {
      ...dispatchModeFilter,
      cptSlabLevels: [Number(activeCptTab)],
      sortOrder,
    };
    if (isStandardDelivery) {
      next.cityScope = cityScope;
    }
    return next;
  }, [dispatchModeFilter, activeCptTab, cityScope, sortOrder, isStandardDelivery]);

  const calculateStats = (data: any[], calculateEntity: string) => {
    const calculatedData = data.reduce(
      (acc, entity) => acc + (entity[calculateEntity] || 0),
      0
    );

    setStats((prev) => ({
      ...prev,
      [calculateEntity === "totalDeliveredShipments"
        ? "totalDeliveredShipmentsCount"
        : calculateEntity === "totalUndeliveredShipments"
        ? "totalUndeliveredShipmentsCount"
        : "totalCancelledShipmentsCount"]: calculatedData,
    }));
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const deliveredData = await getFirstLevelEntities(
          grouping,
          "delivered",
          filters
        );
        const cancelledData = await getFirstLevelEntities(
          grouping,
          "cancelled",
          filters
        );
        calculateStats(deliveredData, "totalDeliveredShipments");
        calculateStats(cancelledData, "cancelledShipmentsCount");
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };

    if (isApiConfigReady) {
      fetchStats();
    }
  }, [grouping, isApiConfigReady, filters]);

  useEffect(() => {
    const fetchFirstLevelEntities = async () => {
      try {
        setIsLoading(true);
        const data = await getFirstLevelEntities(grouping, activeTab, filters);
        setFirstLevelEntities(data);
        calculateStats(
          data,
          activeTab === "undelivered"
            ? "totalUndeliveredShipments"
            : activeTab === "delivered"
            ? "totalDeliveredShipments"
            : "cancelledShipmentsCount"
        );
      } catch (error) {
        console.error("Error fetching first level entities:", error);
        toast({
          title: "Error",
          description: `Failed to fetch ${groupingLabel(grouping)}`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (isApiConfigReady) {
      fetchFirstLevelEntities();
    }
  }, [grouping, activeTab, toast, isApiConfigReady, filters]);

  const toggleFirstLevelExpansion = (entityId: string) => {
    setExpandedFirstLevel((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(entityId)) {
        newSet.delete(entityId);
      } else {
        newSet.add(entityId);
      }
      return newSet;
    });
  };

  const handleGroupingChange = (value: string) => {
    setGrouping(value as ShipmentGrouping);
    setExpandedFirstLevel(new Set());
  };

  const handleTabChange = (index: number) => {
    const newTab =
      index === 0 ? "undelivered" : index === 1 ? "delivered" : "cancelled";
    setActiveTab(newTab);
    setExpandedFirstLevel(new Set());
  };

  const handleCptTabChange = (index: number) => {
    const level = sortedLevelKeys[index];
    if (!level) return;
    setActiveCptTab(level);
    setExpandedFirstLevel(new Set());
    if (Number(level) === STANDARD_CPT_SLAB_LEVEL) {
      setCityScope("intra");
    }
  };

  const handleCityScopeChange = (index: number) => {
    setCityScope(index === 0 ? "intra" : "inter");
    setExpandedFirstLevel(new Set());
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    setExpandedFirstLevel(new Set());
  };

  useEffect(() => {
    setGlobalApiConfig({
      baseUrl,
      authToken,
      clientId,
      customFilters,
    });
    setIsApiConfigReady(true);
  }, [baseUrl, authToken, clientId, customFilters]);

  const tabSelectedStyles = {
    bg: "blue.50",
    color: "blue.700",
    borderColor: "blue.200",
    fontWeight: "600",
  };

  return (
    <ApiConfigProvider
      baseUrl={baseUrl}
      authToken={authToken}
      clientId={clientId}
      customFilters={customFilters}
    >
      <VStack h="full" spacing={0} align="stretch" bg="gray.50">
        <Box
          bg="linear-gradient(135deg, #dbeafe 0%, #93c5fd 50%, #60a5fa 100%)"
          borderBottom="1px"
          borderColor="gray.100"
          px={4}
          py={4}
          shadow="0 2px 8px rgba(0, 0, 0, 0.06)"
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="-100px"
            right="-100px"
            opacity={0.09}
            bg="linear-gradient(135deg, #64748b 0%, #475569 100%)"
            borderRadius="full"
            transform="translate(50px, -50px)"
          />
          <Box
            position="absolute"
            bottom="-80px"
            left="-80px"
            opacity={0.02}
            bg="linear-gradient(135deg, #475569 0%, #334155 100%)"
            borderRadius="full"
            transform="translate(-50px, 50px)"
          />

          <VStack spacing={6} align="stretch" position="relative" zIndex={1}>
            <HStack justify="space-between" align="flex-start">
              <VStack align="start" spacing={3}>
                <HStack spacing={4} align="center" mt={"10px"} ml={"2rem"}>
                  <Icon as={FaTruck} boxSize={7} color="blue.500" />
                  <VStack align="start" spacing={2}>
                    <Heading
                      as="h1"
                      size="xl"
                      color="gray.800"
                      fontWeight="700"
                      letterSpacing="-0.025em"
                    >
                      Shipments
                    </Heading>
                  </VStack>
                </HStack>
              </VStack>
              <HStack
                spacing={8}
                display={{ base: "none", lg: "flex" }}
                align="center"
                mr={"2rem"}
              >
                <VStack spacing={1} align="center">
                  <HStack spacing={2} align="center">
                    <Icon as={FaTruck} color="blue.500" boxSize={4} />
                    <Text fontSize="sm" color="gray.600" fontWeight="600">
                      In Progress
                    </Text>
                  </HStack>
                  <Text fontSize="2xl" fontWeight="700" color="gray.800">
                    {stats.totalUndeliveredShipmentsCount}
                  </Text>
                </VStack>

                <Box w="1px" h="8" bg="gray.600" />

                <VStack spacing={1} align="center">
                  <HStack spacing={2} align="center">
                    <Icon as={FcShipped} color="green.500" boxSize={5} />
                    <Text fontSize="sm" color="gray.600" fontWeight="600">
                      Delivered
                    </Text>
                  </HStack>
                  <Text fontSize="2xl" fontWeight="700" color="gray.800">
                    {stats.totalDeliveredShipmentsCount}
                  </Text>
                </VStack>

                <Box w="1px" h="8" bg="gray.600" />

                <VStack spacing={1} align="center">
                  <HStack spacing={2} align="center">
                    <Box position="relative">
                      <Icon as={FaTruck} color="red.500" boxSize={4} />
                      <Icon
                        as={FaTimes}
                        color="red.500"
                        boxSize={2}
                        position="absolute"
                        top="30%"
                        left="30%"
                        transform="translate(-50%, -50%)"
                        bg="white"
                      />
                    </Box>
                    <Text fontSize="sm" color="gray.600" fontWeight="600">
                      Cancelled
                    </Text>
                  </HStack>
                  <Text fontSize="2xl" fontWeight="700" color="gray.800">
                    {stats.totalCancelledShipmentsCount}
                  </Text>
                </VStack>
              </HStack>
            </HStack>
          </VStack>
        </Box>

        <Box px={12} py={4}>
          <VStack spacing={3} align="stretch">
            <HStack
              justify="space-between"
              align="center"
              flexWrap="nowrap"
              spacing={4}
            >
              <Tabs
                index={
                  activeTab === "undelivered"
                    ? 0
                    : activeTab === "delivered"
                    ? 1
                    : 2
                }
                onChange={handleTabChange}
                variant="enclosed"
                colorScheme="blue"
              >
                <TabList>
                  <Tab
                    _selected={tabSelectedStyles}
                    _hover={{ bg: "blue.50" }}
                    cursor="pointer"
                  >
                    <HStack spacing={2}>
                      <Icon as={FaTruck} />
                      <Text fontSize={"sm"}>In Progress</Text>
                    </HStack>
                  </Tab>
                  <Tab
                    _selected={{
                      bg: "green.50",
                      color: "green.700",
                      borderColor: "green.200",
                      fontWeight: "600",
                    }}
                    _hover={{ bg: "blue.50" }}
                    cursor="pointer"
                  >
                    <HStack spacing={2}>
                      <Box position="relative">
                        <Icon as={FcShipped} boxSize={5} />
                      </Box>
                      <Text fontSize={"sm"}>Delivered</Text>
                    </HStack>
                  </Tab>
                  <Tab
                    _selected={{
                      bg: "red.50",
                      color: "red.700",
                      borderColor: "red.200",
                      fontWeight: "600",
                    }}
                    _hover={{ bg: "blue.50" }}
                    cursor="pointer"
                  >
                    <HStack spacing={2}>
                      <Box position="relative">
                        <Icon as={FaTruck} color="red.500" boxSize={4} />
                        <Icon
                          as={FaTimes}
                          color="red.500"
                          boxSize={2}
                          position="absolute"
                          top="30%"
                          left="30%"
                          transform="translate(-50%, -50%)"
                          bg="white"
                        />
                      </Box>
                      <Text fontSize={"sm"}>Cancelled</Text>
                    </HStack>
                  </Tab>
                </TabList>
              </Tabs>

              <HStack spacing={6} align="center" flexWrap="nowrap">
                <ShipmentFiltersBar
                  statusFilter={activeTab}
                  isApiConfigReady={isApiConfigReady}
                  filters={dispatchModeFilter}
                  onFiltersChange={setDispatchModeFilter}
                />

                <Box w="1px" h="6" bg="gray.300" />

                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={
                    <Icon
                      as={
                        sortOrder === "desc"
                          ? FaSortAmountDown
                          : FaSortAmountUp
                      }
                    />
                  }
                  onClick={toggleSortOrder}
                  cursor="pointer"
                  bg="white"
                >
                  <Text fontSize="sm" fontWeight="500">
                    Shipment Date{" "}
                    {sortOrder === "desc" ? "(Newest)" : "(Oldest)"}
                  </Text>
                </Button>

                <Box w="1px" h="6" bg="gray.300" />

                <HStack spacing={3} align="center" flexWrap="nowrap">
                  <Text
                    fontSize={"sm"}
                    fontWeight="500"
                    color="gray.800"
                    whiteSpace="nowrap"
                  >
                    Group By:
                  </Text>
                  <RadioGroup value={grouping} onChange={handleGroupingChange}>
                    <HStack spacing={4}>
                      <Radio value="customer" colorScheme="blue" cursor="pointer">
                        <Text fontWeight="500" color="gray.800" fontSize={"sm"}>
                          Customer
                        </Text>
                      </Radio>
                      <Radio value="supplier" colorScheme="blue" cursor="pointer">
                        <Text fontWeight="500" color="gray.800" fontSize={"sm"}>
                          Supplier
                        </Text>
                      </Radio>
                      <Radio value="rider" colorScheme="blue" cursor="pointer">
                        <Text fontWeight="500" color="gray.800" fontSize={"sm"}>
                          Rider
                        </Text>
                      </Radio>
                    </HStack>
                  </RadioGroup>
                </HStack>
              </HStack>
            </HStack>

            {sortedLevelKeys.length > 0 && (
              <HStack spacing={4} align="center" flexWrap="wrap">
                <Tabs
                  index={Math.max(0, sortedLevelKeys.indexOf(activeCptTab))}
                  onChange={handleCptTabChange}
                  variant="enclosed"
                  colorScheme="blue"
                  size="sm"
                >
                  <TabList>
                    {sortedLevelKeys.map((level) => (
                      <Tab
                        key={level}
                        _selected={tabSelectedStyles}
                        _hover={{ bg: "blue.50" }}
                        cursor="pointer"
                        display="flex"
                        alignItems="center"
                        gap={2}
                      >
                        <span role="img" aria-label="cpt" style={{ fontSize: 16 }}>
                          {CPT_SLAB_META[level]?.icon || "❓"}
                        </span>
                        {levels?.[level] ||
                          CPT_SLAB_META[level]?.fallbackLabel ||
                          "?"}
                      </Tab>
                    ))}
                  </TabList>
                </Tabs>

                {isStandardDelivery && (
                  <Tabs
                    index={cityScope === "intra" ? 0 : 1}
                    onChange={handleCityScopeChange}
                    variant="soft-rounded"
                    colorScheme="teal"
                    size="sm"
                  >
                    <TabList>
                      <Tab cursor="pointer">Intra City</Tab>
                      <Tab cursor="pointer">Inter City</Tab>
                    </TabList>
                  </Tabs>
                )}
              </HStack>
            )}
          </VStack>
        </Box>

        <Box flex={1} bg="gray.50" overflowY="auto" px={8} py={4}>
          <VStack spacing={6} align="stretch">
            {isLoading ? (
              <Center py={8}>
                <VStack spacing={4}>
                  <Spinner size="lg" color="blue.500" />
                  <Text color="gray.600" fontSize="sm">
                    Loading {groupingLabel(grouping)}...
                  </Text>
                </VStack>
              </Center>
            ) : firstLevelEntities.length === 0 ? (
              <Center py={8}>
                <VStack spacing={3}>
                  <Box p={4} bg="gray.100" borderRadius="full">
                    <Icon
                      as={
                        activeTab === "undelivered"
                          ? FaTruck
                          : activeTab === "delivered"
                          ? FcShipped
                          : FaTruck
                      }
                      boxSize={6}
                      color="gray.400"
                    />
                  </Box>
                  <Text color="gray.500" fontSize="sm">
                    No{" "}
                    {activeTab === "undelivered"
                      ? "undelivered"
                      : activeTab === "delivered"
                      ? "delivered"
                      : "cancelled"}{" "}
                    shipments found
                  </Text>
                </VStack>
              </Center>
            ) : (
              firstLevelEntities.map((entity) => (
                <FirstLevelCard
                  key={entity._id}
                  entity={entity}
                  expanded={expandedFirstLevel.has(entity._id)}
                  onToggleExpansion={() =>
                    toggleFirstLevelExpansion(entity._id)
                  }
                  isLoading={false}
                  grouping={grouping}
                  statusFilter={activeTab}
                  filters={filters}
                />
              ))
            )}
          </VStack>
        </Box>
      </VStack>
    </ApiConfigProvider>
  );
}
